import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Divider, Grid, MenuItem, Select, Typography } from '@mui/material';
import { Cropper, CropperRef } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import { PreviewFile } from '@juno/client-api/fakeModel';
import { DrawerFooter } from '../RightExpandableDrawer';
import { ASPECT_RATIO_MAP, AspectRatioType } from './constants';

interface ImageEditorProps {
  imageFile: PreviewFile;
  aspectRatios?: AspectRatioType[];
  editCallback: (editedImage: PreviewFile) => void;
  onCancel: () => void;
  recommendedSize?: string;
  imageType?: string;
  defaultAspectRatio?: AspectRatioType;
}

const ImageEditor: React.FC<ImageEditorProps> = ({
  imageFile,
  aspectRatios,
  editCallback,
  onCancel,
  recommendedSize,
  imageType,
  defaultAspectRatio,
}) => {
  const cropperRef = useRef<CropperRef>(null);

  const availableAspectRatios = aspectRatios
    ? aspectRatios.map((ratio) => ASPECT_RATIO_MAP[ratio]).filter(Boolean)
    : Object.entries(ASPECT_RATIO_MAP).map(([key, value]) => value);

  const [selectedAspectRatio, setSelectedAspectRatio] = useState<number | undefined>(undefined);
  const [originalSize, setOriginalSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [cropSize, setCropSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (cropperRef.current && selectedAspectRatio) {
      const cropper = cropperRef.current;
      const state = cropper.getState();

      if (!state || !state.imageSize) return;

      const { width, height } = state.imageSize;

      let newWidth = width * 0.8;
      let newHeight = newWidth / selectedAspectRatio;

      if (newHeight > height) {
        newHeight = height;
        newWidth = newHeight * selectedAspectRatio;
      }

      cropper.setCoordinates({
        left: (width - newWidth) / 2,
        top: (height - newHeight) / 2,
        width: newWidth,
        height: newHeight,
      });

      cropper.refresh();
      setCropSize({ width: Math.round(newWidth), height: Math.round(newHeight) });
    }
  }, [selectedAspectRatio]);

  useEffect(() => {
    if (!imageFile?.preview) return;
    const img = new Image();
    img.src = imageFile.preview;
    img.onload = () => {
      setOriginalSize({ width: img.width, height: img.height });
      setCropSize({ width: img.width, height: img.height });

      // If only one aspect ratio exists, lock to that ratio
      if (availableAspectRatios.length === 1) {
        setSelectedAspectRatio(availableAspectRatios[0].ratio);
      } else if (defaultAspectRatio) {
        setSelectedAspectRatio(ASPECT_RATIO_MAP[defaultAspectRatio].ratio);
      }
    };
  }, [imageFile?.preview]);

  const onCropChange = useCallback(() => {
    if (!cropperRef.current) return;
    const state = cropperRef.current.getState();
    if (state?.coordinates) {
      setCropSize({
        width: Math.round(state.coordinates.width),
        height: Math.round(state.coordinates.height),
      });
    }
  }, []);

  useEffect(() => {
    const drawer = document.querySelector(
      '.MuiPaper-root.MuiPaper-elevation.MuiPaper-elevation16.MuiDrawer-paper.MuiDrawer-paperAnchorRight',
    ); // Select the MUI Drawer
    if (!drawer || !cropperRef.current) return;

    const observer = new ResizeObserver(() => {
      console.log('Drawer resized'); // Debugging
      setTimeout(() => cropperRef.current?.refresh(), 100); // ⏳ Delay prevents animation glitches
    });

    observer.observe(drawer); // Watch for drawer size changes

    return () => observer.disconnect(); // Cleanup when unmounting
  }, []);

  const saveCroppedImage = () => {
    if (cropperRef.current) {
      const canvas = cropperRef.current.getCanvas();
      if (canvas) {
        canvas.toBlob((blob) => {
          if (blob) {
            const editedFile = new File([blob], imageFile.name, { type: imageFile.type });
            const editedPreviewFile: PreviewFile = Object.assign(editedFile, {
              preview: URL.createObjectURL(blob),
            });
            editCallback(editedPreviewFile);
          }
        }, imageFile.type);
      }
    }
  };

  const bufferSpace = Math.min(100, originalSize.height * 0.25);
  const previewHeight = Math.min(originalSize.height + bufferSpace, 400);

  if (!imageFile?.preview) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Grid container spacing={2} px={1} alignItems='center'>
        {recommendedSize && (
          <>
            <Grid item xs={9}>
              <Typography variant='subtitle1'>Recommended Size</Typography>
              <Typography variant='body2'>
                The recommmended size for {imageType} images is:
              </Typography>
            </Grid>
            <Grid item xs={3} textAlign='right'>
              <Typography variant='subtitle1' fontWeight={400}>
                {recommendedSize}
              </Typography>
              <Typography variant='body2'></Typography>
            </Grid>
          </>
        )}
        {/* Original Size */}
        <Grid item xs={9}>
          <Typography variant='subtitle1' fontWeight={600}>
            Original Size
          </Typography>
          <Typography variant='body2'>The uploaded image's dimensions.</Typography>
        </Grid>
        <Grid item xs={3} textAlign='right'>
          <Typography variant='subtitle1' fontWeight={400}>
            {originalSize.width} x {originalSize.height} px
          </Typography>
        </Grid>

        {/* Crop Size */}
        <Grid item xs={9}>
          <Typography variant='subtitle1' fontWeight={600}>
            Crop Size
          </Typography>
          <Typography variant='body2'>The size of your cropped selection.</Typography>
        </Grid>
        <Grid item xs={3} textAlign='right'>
          <Typography variant='subtitle1' fontWeight={400}>
            {cropSize.width} x {cropSize.height} px
          </Typography>
        </Grid>

        {/* Aspect Ratio */}
        <Grid item xs={9}>
          <Typography variant='subtitle1' fontWeight={600}>
            Aspect Ratio
          </Typography>
          <Typography variant='body2'>
            {availableAspectRatios.length > 1
              ? 'Choose a fixed ratio or keep it free.'
              : 'The aspect ratio is locked and cannot be changed.'}
          </Typography>
        </Grid>
        <Grid item xs={3} textAlign='right'>
          <Select
            disabled={availableAspectRatios.length === 1}
            size='small'
            value={selectedAspectRatio?.toString() || ''}
            onChange={(e) => {
              const newRatio = parseFloat(e.target.value);
              setSelectedAspectRatio(newRatio);
            }}
            displayEmpty
            sx={{
              width: '100%',
              bgcolor: 'background.paper',
              borderRadius: '4px',
              '& .MuiSelect-select': { padding: '6px 12px' },
            }}
          >
            <MenuItem value=''>Free</MenuItem>
            {availableAspectRatios.map((ratio) => (
              <MenuItem key={ratio.value} value={ratio.ratio.toString()}>
                {ratio.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>

      <Box sx={{ textAlign: 'center', mb: 2 }}></Box>
      <Cropper
        ref={cropperRef}
        src={imageFile.preview}
        style={{ backgroundColor: '#f0f0f0', height: `${previewHeight}px`, width: '100%' }}
        stencilProps={{
          aspectRatio: selectedAspectRatio,
        }}
        onChange={onCropChange}
      />
      <Box sx={{ width: '100%' }}>
        <DrawerFooter
          onSubmit={saveCroppedImage}
          onCancel={onCancel}
          submitting={false}
          disabled={false}
        />
      </Box>
    </Box>
  );
};

export default ImageEditor;
